import { renderIconToElement } from "@platform/design-system";

import type { ReviewMenuTarget } from "./reviewMenuItems";
import type { DefaultMenuItem, MenuItemDef } from "ag-grid-community";

export interface ValidationMenuCallbacks {
  isValidated(planId: string, fieldPath: string): boolean;
  /** Text of the cell's comment, empty when it has none. */
  getComment(planId: string, fieldPath: string): string;
  /** Applies the flag to every target, skipping those already in that state. */
  onSetValidated(targets: ReviewMenuTarget[], isValidated: boolean): void;
  onEditComment(planId: string, fieldPath: string, comment: string): void;
  onClearComment(planId: string, fieldPath: string): void;
}

export interface ValidationMenuLabels {
  validate: string;
  clear: string;
}

const CELL_VALIDATION_LABELS: ValidationMenuLabels = {
  validate: "Validate Value",
  clear: "Clear Validation",
};

const createCommentItems = (
  { planId, fieldPath }: ReviewMenuTarget,
  callbacks: ValidationMenuCallbacks,
): MenuItemDef[] => {
  const comment = callbacks.getComment(planId, fieldPath);
  if (!comment) return [];

  return [
    {
      name: "Edit/Review Comment",
      icon: renderIconToElement("comment", "sm", "muted"),
      action: () => callbacks.onEditComment(planId, fieldPath, comment),
    },
    {
      name: "Clear Comment",
      icon: renderIconToElement("remove", "sm", "muted"),
      action: () => callbacks.onClearComment(planId, fieldPath),
    },
  ];
};

/**
 * Cell-menu items for the validation flag over one or more cells: "Validate
 * Value" while any cell is unvalidated, "Clear Validation" while any is
 * validated, both on a mixed selection. Either action applies to every cell.
 *
 * A validated cell keeps the comment it was reviewed with, and the review items
 * no longer cover it, so a single validated cell carries its own comment
 * actions. Both leave the cell validated: editing or clearing the text is not a
 * review. They open a per-cell dialog, so a multi-cell menu leaves them out.
 *
 * Each surface resolves the cells' identity its own way — from the AG Grid
 * columns and rows in the plan editor, from a DOM attribute in the rates grid —
 * and hands the resolved targets here.
 */
export const createValidationMenuItems = (
  targets: ReviewMenuTarget[],
  callbacks: ValidationMenuCallbacks,
  labels: ValidationMenuLabels = CELL_VALIDATION_LABELS,
): (DefaultMenuItem | MenuItemDef)[] => {
  const isTargetValidated = ({ planId, fieldPath }: ReviewMenuTarget) =>
    callbacks.isValidated(planId, fieldPath);
  const hasUnvalidated = !targets.every(isTargetValidated);
  const hasValidated = targets.some(isTargetValidated);
  const [singleValidatedTarget] =
    targets.length === 1 && hasValidated ? targets : [];

  const setAll = (isValidated: boolean) => () =>
    callbacks.onSetValidated(targets, isValidated);

  return [
    ...(hasUnvalidated
      ? [
          {
            name: labels.validate,
            icon: renderIconToElement("success", "sm", "muted"),
            action: setAll(true),
          },
        ]
      : []),
    ...(singleValidatedTarget
      ? createCommentItems(singleValidatedTarget, callbacks)
      : []),
    ...(hasValidated
      ? [
          {
            name: labels.clear,
            icon: renderIconToElement("remove", "sm", "muted"),
            action: setAll(false),
          },
        ]
      : []),
  ];
};
